import React, { useState } from 'react'
import { Button } from './ui'
import { Volume2, Mic, MicOff, Settings, X, Type, Languages, BookOpen, Drum, Sun, Pause, Play, Monitor } from 'lucide-react'
import { cn } from '../lib/utils'

const Toggle = ({ active, onToggle, disabled = false, ariaLabel }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={() => {
      if (!disabled && onToggle) onToggle()
    }}
    disabled={disabled}
    className={cn(
      'relative h-6 w-12 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:cursor-not-allowed disabled:opacity-40',
      active ? 'bg-gray-500' : 'bg-white/20'
    )}
  >
    <span
      className={cn(
        'pointer-events-none absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200',
        active ? 'translate-x-6' : 'translate-x-0'
      )}
    />
  </button>
)

const PracticeAudioControls = ({
  pitchShift,
  onPitchShiftChange,
  pitchShiftEnabled = true,
  onPitchShiftEnabledChange,
  vocalVolume,
  accompanimentVolume,
  drumsVolume = 2.0,
  vocalMuted,
  accompanimentMuted,
  drumsMuted = false,
  onVocalVolumeChange,
  onAccompanimentVolumeChange,
  onDrumsVolumeChange,
  onVocalMuteToggle,
  onAccompanimentMuteToggle,
  onDrumsMuteToggle,
  audioEngineReady,
  audioEngineError,
  getKeyDisplay,
  showLyrics,
  showTranslation,
  showRomanization,
  onShowLyricsToggle,
  onShowTranslationToggle,
  onShowRomanizationToggle,
  forceLineMode = false,
  onForceLineModeToggle,
  isStudioMode = false,
  backgroundBrightness = 100,
  animationPaused = false,
  useSimpleBackground = false,
  onBackgroundBrightnessChange,
  onAnimationPausedToggle,
  onSimpleBackgroundToggle
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const convertVolumeToSliderValue = (volume) => volume * 50
  const convertSliderValueToVolume = (sliderValue) => sliderValue / 50
  const formatVolumeDisplay = (volume) => Math.round(volume * 50)

  if (!audioEngineReady && !audioEngineError) return null

  return (
    <>
      {/* Settings Cogwheel Trigger */}
      <Button
        type="button"
        aria-label="Open audio settings"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-6 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all z-50"
      >
        <Settings className="w-6 h-6 text-white" />
      </Button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed bottom-20 left-6 bg-black/50 backdrop-blur-sm rounded-lg p-6 max-w-md z-50 border border-white/20">
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold text-lg">Audio Settings</h3>
            <Button
              onClick={() => setIsOpen(false)}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="space-y-6">
            {/* Pitch Shift Control */}
            <div>
              <h4 className="text-white/80 text-sm font-medium mb-3">Key Control</h4>
              <div className="mb-3 flex items-center justify-between gap-4">
                <div>
                  <p className="text-white/80 text-sm">Pitch shifting</p>
                  <p className="text-white/45 text-xs">
                    {pitchShiftEnabled ? 'Enabled' : 'Off — raw stems mixed directly'}
                  </p>
                </div>
                <Toggle
                  active={pitchShiftEnabled}
                  onToggle={() => onPitchShiftEnabledChange?.(!pitchShiftEnabled)}
                  ariaLabel="Toggle pitch shifting"
                />
              </div>
              {pitchShiftEnabled ? (
                <div className="flex items-center space-x-4">
                  <Volume2 className="w-5 h-5 text-white/80" />
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => onPitchShiftChange(pitchShift - 1)}
                      className="bg-white/20 hover:bg-white/30 rounded px-3 py-1 text-white text-sm"
                    >
                      -
                    </Button>
                    <span className="text-white font-mono min-w-[3rem] text-center">
                      {pitchShift > 0 ? '+' : ''}{pitchShift}
                    </span>
                    <Button
                      onClick={() => onPitchShiftChange(pitchShift + 1)}
                      className="bg-white/20 hover:bg-white/30 rounded px-3 py-1 text-white text-sm"
                    >
                      +
                    </Button>
                  </div>
                  <span className="text-white/60 text-sm">
                    Key: {getKeyDisplay() || 'Unknown'}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-white/55 text-sm">
                  <Volume2 className="w-5 h-5" />
                  <span>Raw playback — key controls unavailable</span>
                </div>
              )}
              {pitchShiftEnabled && (
                <p className="mt-3 text-xs text-white/45">
                  Powered by Signalsmith Stretch.
                </p>
              )}
            </div>

            {/* Error Display */}
            {audioEngineError && (
              <div className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500/30 text-red-300 border border-red-400">
                🚨 Audio Engine Error: {audioEngineError}
              </div>
            )}

            {/* Volume Controls */}
            <div>
              <h4 className="text-white/80 text-sm font-medium mb-3">Track Volumes</h4>
              <div className="space-y-4">
                {/* Vocal Track Volume */}
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={onVocalMuteToggle}
                    className={`p-2 rounded-full transition-all ${
                      vocalMuted
                        ? 'bg-red-500/30 text-red-300'
                        : 'bg-white/20 hover:bg-white/30 text-white'
                    }`}
                  >
                    {vocalMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                  <span className="text-white/80 text-sm min-w-[4rem]">Vocals</span>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    step="2.5"
                    value={convertVolumeToSliderValue(vocalVolume)}
                    onChange={(e) => onVocalVolumeChange(convertSliderValueToVolume(parseFloat(e.target.value)))}
                    className="flex-1 volume-slider"
                  />
                  <span className="text-white/60 text-xs min-w-[3rem]">
                    {formatVolumeDisplay(vocalVolume)}%
                  </span>
                </div>

                {/* Accompaniment Track Volume */}
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={onAccompanimentMuteToggle}
                    className={`p-2 rounded-full transition-all ${
                      accompanimentMuted
                        ? 'bg-red-500/30 text-red-300'
                        : 'bg-white/20 hover:bg-white/30 text-white'
                    }`}
                  >
                    <Volume2 className="w-4 h-4" />
                  </Button>
                  <span className="text-white/80 text-sm min-w-[4rem]">Music</span>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    step="2.5"
                    value={convertVolumeToSliderValue(accompanimentVolume)}
                    onChange={(e) => onAccompanimentVolumeChange(convertSliderValueToVolume(parseFloat(e.target.value)))}
                    className="flex-1 volume-slider"
                  />
                  <span className="text-white/60 text-xs min-w-[3rem]">
                    {formatVolumeDisplay(accompanimentVolume)}%
                  </span>
                </div>

                {/* Drums Track Volume */}
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={onDrumsMuteToggle}
                    className={`p-2 rounded-full transition-all ${
                      drumsMuted
                        ? 'bg-red-500/30 text-red-300'
                        : 'bg-white/20 hover:bg-white/30 text-white'
                    }`}
                  >
                    <Drum className="w-4 h-4" />
                  </Button>
                  <span className="text-white/80 text-sm min-w-[4rem]">Drums</span>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    step="2.5"
                    value={convertVolumeToSliderValue(drumsVolume)}
                    onChange={(e) =>
                      onDrumsVolumeChange &&
                      onDrumsVolumeChange(convertSliderValueToVolume(parseFloat(e.target.value)))
                    }
                    className="flex-1 volume-slider"
                  />
                  <span className="text-white/60 text-xs min-w-[3rem]">
                    {formatVolumeDisplay(drumsVolume)}%
                  </span>
                </div>
              </div>

            </div>

            {/* Lyrics Display Options */}
            <div>
              <h4 className="text-white/80 text-sm font-medium mb-3">Lyrics Display</h4>
              <div className="space-y-3">
                {/* Show Lyrics Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Type className="w-4 h-4 text-white/80" />
                    <span className="text-white/80 text-sm">Show Lyrics</span>
                  </div>
                  <Toggle active={showLyrics} onToggle={onShowLyricsToggle} />
                </div>

                {/* Show Romanization Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <BookOpen className="w-4 h-4 text-white/80" />
                    <span className="text-white/80 text-sm">Show Romanization</span>
                  </div>
                  <Toggle active={showRomanization} onToggle={onShowRomanizationToggle} />
                </div>

                {/* Show Translation Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Languages className="w-4 h-4 text-white/80" />
                    <span className="text-white/80 text-sm">Show Translation</span>
                  </div>
                  <Toggle active={showTranslation} onToggle={onShowTranslationToggle} />
                </div>

                {/* Force Line Mode Toggle - only show when not in studio mode */}
                {!isStudioMode && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Type className="w-4 h-4 text-white/80" />
                      <span className="text-white/80 text-sm">Line Mode (vs Word)</span>
                    </div>
                    <Toggle active={forceLineMode} onToggle={onForceLineModeToggle} />
                  </div>
                )}
              </div>
            </div>

            {/* Visual Settings */}
            <div>
              <h4 className="text-white/80 text-sm font-medium mb-3">Visual Settings</h4>
              <div className="space-y-4">
                {/* Background Brightness Control */}
                <div className="flex items-center space-x-3">
                  <Sun className="w-4 h-4 text-white/80" />
                  <span className="text-white/80 text-sm min-w-[4rem]">Brightness</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={backgroundBrightness}
                    onChange={(e) => onBackgroundBrightnessChange && onBackgroundBrightnessChange(parseInt(e.target.value))}
                    className="flex-1 volume-slider"
                  />
                  <span className="text-white/60 text-xs min-w-[3rem]">
                    {backgroundBrightness}%
                  </span>
                </div>

                {/* Animation Pause Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {animationPaused ? <Pause className="w-4 h-4 text-white/80" /> : <Play className="w-4 h-4 text-white/80" />}
                    <span className="text-white/80 text-sm">Background Animation</span>
                  </div>
                  <Toggle active={!animationPaused} onToggle={onAnimationPausedToggle} />
                </div>

                {/* Feeling Boring Background Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Monitor className="w-4 h-4 text-white/80" />
                    <span className="text-white/80 text-sm">Feeling Boring Background</span>
                  </div>
                  <Toggle active={useSimpleBackground} onToggle={onSimpleBackgroundToggle} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PracticeAudioControls
